// QueryPanel - File selector and query input for visualization
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Play, Loader2, FileSpreadsheet, ChevronDown, ChevronUp, Search, X, Check, Box, Filter, Layers } from 'lucide-react';
import { ThemeType } from '../../types/visualization';
import { THEMES } from '../../constants/visualizationThemes';

interface QueryPanelProps {
  files: { name: string; key: string; isMultipart?: boolean }[];
  selectedFile: string;
  onFileSelect: (fileKey: string) => void;
  query: string;
  onQueryChange: (query: string) => void;
  onExecute: () => void;
  isLoading: boolean;
  filesLoading?: boolean;
  theme: ThemeType;
  recordCount?: number;
  hasData?: boolean;
  columns?: string[];
  isLoadingColumns?: boolean;
}

// Helper to clean file names for display - hide internal implementation details
const getDisplayName = (fileName: string): string => {
  let name = fileName;
  // Remove common extensions
  name = name.replace(/\.(parquet|csv|json|xlsx|xls|tsv)$/i, '');
  // Remove double extensions like .csv.parquet
  name = name.replace(/\.(csv|json)\.(parquet)$/i, '');
  // Remove timestamp prefix (e.g., "1765940893209-")
  if (/^\d+-/.test(name)) {
    name = name.replace(/^\d+-/, '');
  }
  // Remove date-time suffix (e.g., "_20231215_143022")
  name = name.replace(/_\d{8}_\d{6}$/, '');
  // Remove "(X files)" or "(X parts)" suffixes
  name = name.replace(/\s*\(\d+\s+(files?|parts?)\)$/i, '');
  // Remove "_part_X" or "_chunk_X" suffixes
  name = name.replace(/[_-](part|chunk)[_-]?\d+$/i, '');
  // Remove trailing underscores or hyphens
  name = name.replace(/[_-]+$/, '');
  return name;
};

// Row limit options
const ROW_LIMITS = [
  { value: 100, label: '100' },
  { value: 1000, label: '1K' },
  { value: 10000, label: '10K' },
  { value: 50000, label: '50K' },
  { value: 100000, label: '100K' },
];

export default function QueryPanel({
  files,
  selectedFile,
  onFileSelect,
  query,
  onQueryChange,
  onExecute,
  isLoading,
  filesLoading = false,
  theme,
  recordCount,
  hasData = false,
  columns = [],
  isLoadingColumns = false,
}: QueryPanelProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [fileSearchOpen, setFileSearchOpen] = useState(false);
  const [fileSearchTerm, setFileSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [rowLimit, setRowLimit] = useState(10000);
  const [includeFilter, setIncludeFilter] = useState('');
  const [excludeFilter, setExcludeFilter] = useState('');
  const [groupByColumn, setGroupByColumn] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const t = THEMES[theme];

  // Build query with filters and group by
  const buildQuery = (limit: number, include: string, exclude: string, groupBy: string) => {
    const hasFilters = include.trim() || exclude.trim();
    const hasGroupBy = groupBy.trim();

    // Simple case - no filters, no group by
    if (!hasFilters && !hasGroupBy) {
      return `result = df.lazy().head(${limit}).collect()`;
    }

    // Simple group by without filters
    if (!hasFilters && hasGroupBy) {
      return `result = df.lazy().group_by("${groupBy}").agg(pl.count().alias("count")).sort("count", descending=True).head(${limit}).collect()`;
    }

    // Complex case with filters - build a multi-line Python query
    const includeTerms = include.split(',').map(t => t.trim().toLowerCase()).filter(t => t);
    const excludeTerms = exclude.split(',').map(t => t.trim().toLowerCase()).filter(t => t);

    let query = `
# Get schema to identify searchable columns
schema = df.collect_schema()

# Build filter conditions
include_conditions = []
exclude_conditions = []

for col_name in schema.names():
    col_type = schema[col_name]
    if col_type in [pl.Utf8, pl.String, pl.Int8, pl.Int16, pl.Int32, pl.Int64, pl.UInt8, pl.UInt16, pl.UInt32, pl.UInt64, pl.Float32, pl.Float64, pl.Boolean]:
`;

    // Add include conditions
    if (includeTerms.length > 0) {
      includeTerms.forEach(term => {
        const escapedTerm = term.replace(/"/g, '\\"');
        query += `        include_conditions.append(pl.col(col_name).cast(pl.Utf8).fill_null("").str.to_lowercase().str.contains("${escapedTerm}", literal=True))\n`;
      });
    }

    // Add exclude conditions
    if (excludeTerms.length > 0) {
      excludeTerms.forEach(term => {
        const escapedTerm = term.replace(/"/g, '\\"');
        query += `        exclude_conditions.append(pl.col(col_name).cast(pl.Utf8).fill_null("").str.to_lowercase().str.contains("${escapedTerm}", literal=True))\n`;
      });
    }

    query += `
# Apply filters
result_df = df.lazy()
`;

    if (includeTerms.length > 0) {
      query += `if include_conditions:
    result_df = result_df.filter(pl.any_horizontal(include_conditions))
`;
    }

    if (excludeTerms.length > 0) {
      query += `if exclude_conditions:
    result_df = result_df.filter(~pl.any_horizontal(exclude_conditions))
`;
    }

    // Add group by if specified
    if (hasGroupBy) {
      query += `result = result_df.group_by("${groupBy}").agg(pl.count().alias("count")).sort("count", descending=True).head(${limit}).collect()`;
    } else {
      query += `result = result_df.head(${limit}).collect()`;
    }

    return query.trim();
  };

  // Update query when row limit or filters change
  const handleRowLimitChange = (limit: number) => {
    setRowLimit(limit);
    onQueryChange(buildQuery(limit, includeFilter, excludeFilter, groupByColumn));
  };

  // Update query when filters change
  const handleFilterChange = (type: 'include' | 'exclude', value: string) => {
    if (type === 'include') {
      setIncludeFilter(value);
      onQueryChange(buildQuery(rowLimit, value, excludeFilter, groupByColumn));
    } else {
      setExcludeFilter(value);
      onQueryChange(buildQuery(rowLimit, includeFilter, value, groupByColumn));
    }
  };

  // Update query when group by changes
  const handleGroupByChange = (column: string) => {
    setGroupByColumn(column);
    onQueryChange(buildQuery(rowLimit, includeFilter, excludeFilter, column));
  };

  // Filter files based on search term
  const filteredFiles = useMemo(() => {
    if (!fileSearchTerm.trim()) return files;
    const term = fileSearchTerm.toLowerCase();
    return files.filter(f =>
      f.name.toLowerCase().includes(term) ||
      getDisplayName(f.name).toLowerCase().includes(term)
    );
  }, [files, fileSearchTerm]);

  // Reset highlight when filtered list changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredFiles.length]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (fileSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [fileSearchOpen]);

  // Function to open dropdown with correct position
  const openDropdown = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
    setFileSearchOpen(true);
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (listRef.current && highlightedIndex >= 0) {
      const items = listRef.current.children;
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!fileSearchOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(i => Math.min(i + 1, filteredFiles.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(i => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredFiles[highlightedIndex]) {
          onFileSelect(filteredFiles[highlightedIndex].key);
          setFileSearchOpen(false);
          setFileSearchTerm('');
        }
        break;
      case 'Escape':
        e.preventDefault();
        setFileSearchOpen(false);
        setFileSearchTerm('');
        break;
    }
  };

  // Get selected file display name
  const selectedFileName = useMemo(() => {
    const file = files.find(f => f.key === selectedFile);
    return file ? getDisplayName(file.name) : '';
  }, [files, selectedFile]);

  const panelBg = theme === 'dark'
    ? 'linear-gradient(160deg, rgba(28, 32, 42, 0.95) 0%, rgba(16, 18, 24, 0.97) 100%)'
    : 'linear-gradient(160deg, rgba(255, 255, 255, 0.96) 0%, rgba(241, 245, 249, 0.97) 100%)';

  return (
    <div style={{
      position: 'absolute',
      top: '80px',
      left: '16px',
      background: panelBg,
      border: theme === 'dark'
        ? '1px solid rgba(255,255,255,0.1)'
        : '1px solid rgba(0,0,0,0.12)',
      borderRadius: '10px',
      color: t.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      backdropFilter: 'blur(24px) saturate(1.4)',
      width: collapsed ? '160px' : '280px',
      boxShadow: theme === 'dark'
        ? '0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06) inset, 0 0 60px -20px rgba(59,130,246,0.08)'
        : '0 8px 32px rgba(0,0,0,0.08), 0 0 0 1px rgba(255,255,255,0.8) inset, 0 1px 3px rgba(0,0,0,0.06)',
      transition: 'width 0.3s ease',
      zIndex: 100,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div
        onClick={() => setCollapsed(!collapsed)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 14px',
          cursor: 'pointer',
          borderBottom: collapsed ? 'none' : `1px solid ${t.panelBorder}`,
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FileSpreadsheet size={16} color={t.accent} strokeWidth={2} />
          <span style={{ fontSize: '12px', fontWeight: 600, color: t.text, letterSpacing: '0.5px' }}>
            DATA SOURCE
          </span>
          {recordCount !== undefined && (
            <span style={{
              fontSize: '10px',
              color: t.textMuted,
              background: t.inputBg,
              padding: '2px 8px',
              borderRadius: '10px',
            }}>
              {recordCount.toLocaleString()} rows
            </span>
          )}
        </div>
        {collapsed ? (
          <ChevronDown size={16} color={t.textMuted} />
        ) : (
          <ChevronUp size={16} color={t.textMuted} />
        )}
      </div>

      {!collapsed && (
        <div style={{ padding: '10px 14px' }}>
          {/* File Selector - Searchable Dropdown */}
          <div style={{ marginBottom: '12px', position: 'relative' }}>
            <label style={{
              display: 'block',
              marginBottom: '5px',
              color: t.textMuted,
              fontSize: '9px',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              fontWeight: 500,
            }}>
              Select File
            </label>

            {/* Trigger Button */}
            <button
              ref={triggerRef}
              type="button"
              onClick={() => {
                if (!isLoading) {
                  if (fileSearchOpen) {
                    setFileSearchOpen(false);
                  } else {
                    openDropdown();
                  }
                }
              }}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '8px 10px',
                background: t.inputBg,
                border: `1px solid ${fileSearchOpen ? t.accent : t.buttonBorder}`,
                borderRadius: '6px',
                color: selectedFile ? t.text : t.textMuted,
                fontSize: '11px',
                outline: 'none',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.6 : 1,
                transition: 'border-color 0.2s, box-shadow 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                textAlign: 'left',
                boxShadow: fileSearchOpen ? `0 0 0 3px ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` : 'none',
              }}
            >
              <span style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}>
                {selectedFileName || 'Search files...'}
              </span>
              <ChevronDown
                size={16}
                color={t.textMuted}
                style={{
                  transition: 'transform 0.2s',
                  transform: fileSearchOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  flexShrink: 0,
                  marginLeft: '8px',
                }}
              />
            </button>

            {/* Dropdown Panel - rendered via portal to escape overflow:hidden */}
            {fileSearchOpen && createPortal(
              <div
                style={{
                  position: 'fixed',
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                  width: dropdownPosition.width,
                  background: theme === 'dark' ? 'rgba(20, 22, 28, 0.98)' : 'rgba(255, 255, 255, 0.98)',
                  border: `1px solid ${t.panelBorder}`,
                  borderRadius: '8px',
                  boxShadow: theme === 'dark'
                    ? '0 8px 32px rgba(0,0,0,0.5)'
                    : '0 8px 32px rgba(0,0,0,0.15)',
                  zIndex: 10000,
                  overflow: 'hidden',
                  maxHeight: '50vh',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onKeyDown={handleKeyDown}
              >
                {/* Search Input */}
                <div style={{
                  padding: '10px 12px',
                  borderBottom: `1px solid ${t.panelBorder}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <Search size={14} color={t.textMuted} />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={fileSearchTerm}
                    onChange={(e) => setFileSearchTerm(e.target.value)}
                    placeholder="Type to search..."
                    style={{
                      flex: 1,
                      background: 'transparent',
                      border: 'none',
                      outline: 'none',
                      color: t.text,
                      fontSize: '13px',
                    }}
                  />
                  {fileSearchTerm && (
                    <button
                      type="button"
                      onClick={() => setFileSearchTerm('')}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '2px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <X size={14} color={t.textMuted} />
                    </button>
                  )}
                </div>

                {/* File List */}
                <div
                  ref={listRef}
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '4px',
                    minHeight: '60px',
                  }}
                >
                  {filesLoading ? (
                    <div style={{
                      padding: '20px',
                      textAlign: 'center',
                      color: t.textMuted,
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}>
                      <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      Loading files...
                    </div>
                  ) : filteredFiles.length === 0 ? (
                    <div style={{
                      padding: '20px',
                      textAlign: 'center',
                      color: t.textMuted,
                      fontSize: '12px',
                    }}>
                      {files.length === 0 ? 'No files available' : 'No files found'}
                    </div>
                  ) : (
                    filteredFiles.map((file, idx) => {
                      const displayName = getDisplayName(file.name);
                      const isSelected = file.key === selectedFile;
                      const isHighlighted = idx === highlightedIndex;

                      return (
                        <button
                          key={file.key}
                          type="button"
                          onClick={() => {
                            onFileSelect(file.key);
                            setFileSearchOpen(false);
                            setFileSearchTerm('');
                          }}
                          onMouseEnter={() => setHighlightedIndex(idx)}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            background: isHighlighted
                              ? (theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)')
                              : 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            color: t.text,
                            fontSize: '12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            textAlign: 'left',
                            transition: 'background 0.15s',
                          }}
                        >
                          <div style={{
                            width: '18px',
                            height: '18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            {isSelected ? (
                              <Check size={14} color={t.accent} strokeWidth={3} />
                            ) : file.isMultipart ? (
                              <Box size={14} color="#3b82f6" />
                            ) : (
                              <FileSpreadsheet size={14} color={t.textMuted} />
                            )}
                          </div>
                          <div style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                          }}>
                            <div style={{
                              fontWeight: isSelected ? 600 : 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {displayName}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                {/* Footer with count */}
                <div style={{
                  padding: '8px 12px',
                  borderTop: `1px solid ${t.panelBorder}`,
                  fontSize: '10px',
                  color: t.textDimmed,
                  display: 'flex',
                  justifyContent: 'space-between',
                }}>
                  <span>{filteredFiles.length} of {files.length} files</span>
                  <span style={{ color: t.textMuted }}>↑↓ navigate • Enter select • Esc close</span>
                </div>
              </div>,
              document.body
            )}

            {/* Click outside handler - also in portal */}
            {fileSearchOpen && createPortal(
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 9999,
                }}
                onClick={() => {
                  setFileSearchOpen(false);
                  setFileSearchTerm('');
                }}
              />,
              document.body
            )}
          </div>

          {/* Row Limit Selector */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{
              display: 'block',
              marginBottom: '5px',
              color: t.textMuted,
              fontSize: '9px',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              fontWeight: 500,
            }}>
              Rows
            </label>
            <div style={{
              display: 'flex',
              borderRadius: '6px',
              overflow: 'hidden',
              border: `1px solid ${t.buttonBorder}`,
            }}>
              {ROW_LIMITS.map((opt, i) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleRowLimitChange(opt.value)}
                  disabled={isLoading}
                  style={{
                    flex: 1,
                    padding: '6px 0',
                    background: rowLimit === opt.value
                      ? '#3b82f6'
                      : t.inputBg,
                    border: 'none',
                    borderRight: i < ROW_LIMITS.length - 1 ? `1px solid ${t.buttonBorder}` : 'none',
                    color: rowLimit === opt.value ? '#fff' : t.text,
                    fontSize: '10px',
                    fontWeight: rowLimit === opt.value ? 600 : 400,
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: isLoading ? 0.6 : 1,
                    transition: 'background 0.15s, color 0.15s',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Advanced accordion — pre-filter, group by, custom query */}
          <div style={{ marginBottom: '10px' }}>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: (includeFilter || excludeFilter || groupByColumn) ? t.accent : t.textMuted,
                fontSize: '9px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {showAdvanced ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              <Filter size={12} />
              Advanced
              {(includeFilter || excludeFilter || groupByColumn) && (
                <span style={{
                  background: '#22c55e',
                  color: '#000',
                  borderRadius: '10px',
                  padding: '1px 6px',
                  fontSize: '9px',
                  fontWeight: 600,
                  marginLeft: '4px',
                }}>
                  Active
                </span>
              )}
            </button>

            {showAdvanced && (
              <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* Pre-Filter: Include */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    color: t.textMuted,
                    fontSize: '10px',
                    letterSpacing: '0.5px',
                  }}>
                    <span style={{ color: '#22c55e' }}>Include</span> rows containing (comma separated)
                  </label>
                  <input
                    type="text"
                    value={includeFilter}
                    onChange={(e) => handleFilterChange('include', e.target.value)}
                    placeholder="term1, term2, term3..."
                    disabled={isLoading || hasData}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: t.inputBg,
                      border: `1px solid ${includeFilter ? '#22c55e' : t.buttonBorder}`,
                      borderRadius: '6px',
                      color: t.text,
                      fontSize: '12px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      opacity: (isLoading || hasData) ? 0.6 : 1,
                    }}
                  />
                </div>

                {/* Pre-Filter: Exclude */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    color: t.textMuted,
                    fontSize: '10px',
                    letterSpacing: '0.5px',
                  }}>
                    <span style={{ color: '#ef4444' }}>Exclude</span> rows containing (comma separated)
                  </label>
                  <input
                    type="text"
                    value={excludeFilter}
                    onChange={(e) => handleFilterChange('exclude', e.target.value)}
                    placeholder="term1, term2, term3..."
                    disabled={isLoading || hasData}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: t.inputBg,
                      border: `1px solid ${excludeFilter ? '#ef4444' : t.buttonBorder}`,
                      borderRadius: '6px',
                      color: t.text,
                      fontSize: '12px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      opacity: (isLoading || hasData) ? 0.6 : 1,
                    }}
                  />
                </div>

                {/* Group By Column */}
                <div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginBottom: '8px',
                    color: t.textMuted,
                    fontSize: '10px',
                    letterSpacing: '1.2px',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                  }}>
                    <Layers size={12} />
                    Group By Column
                    {columns.length > 0 && !groupByColumn && (
                      <span style={{
                        background: t.inputBg,
                        color: t.textMuted,
                        borderRadius: '10px',
                        padding: '1px 6px',
                        fontSize: '9px',
                        fontWeight: 500,
                        textTransform: 'none',
                        letterSpacing: 'normal',
                      }}>
                        {columns.length} columns
                      </span>
                    )}
                    {groupByColumn && (
                      <span style={{
                        background: '#8b5cf6',
                        color: '#000',
                        borderRadius: '10px',
                        padding: '1px 6px',
                        fontSize: '9px',
                        fontWeight: 600,
                        textTransform: 'none',
                        letterSpacing: 'normal',
                      }}>
                        Active
                      </span>
                    )}
                  </label>
                  <select
                    value={groupByColumn}
                    onChange={(e) => handleGroupByChange(e.target.value)}
                    disabled={isLoading || hasData || isLoadingColumns || columns.length === 0}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: t.inputBg,
                      border: `1px solid ${groupByColumn ? '#8b5cf6' : t.buttonBorder}`,
                      borderRadius: '6px',
                      color: t.text,
                      fontSize: '12px',
                      outline: 'none',
                      cursor: (isLoading || hasData || isLoadingColumns || columns.length === 0) ? 'not-allowed' : 'pointer',
                      opacity: (isLoading || hasData || isLoadingColumns) ? 0.6 : 1,
                      appearance: 'none',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='${theme === 'dark' ? '%23888' : '%23666'}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 12px center',
                      paddingRight: '36px',
                    }}
                  >
                    {isLoadingColumns ? (
                      <option value="">Loading columns...</option>
                    ) : columns.length === 0 ? (
                      <option value="">Select a file first</option>
                    ) : (
                      <>
                        <option value="">None (show all rows)</option>
                        {columns.map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </>
                    )}
                  </select>
                </div>

                {/* Custom Query */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    color: t.textMuted,
                    fontSize: '10px',
                    letterSpacing: '0.5px',
                  }}>
                    Custom Polars Query
                  </label>
                  <textarea
                    value={query}
                    onChange={(e) => onQueryChange(e.target.value)}
                    disabled={isLoading}
                    placeholder="result = df.lazy().head(10000).collect()"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: t.inputBg,
                      border: `1px solid ${t.buttonBorder}`,
                      borderRadius: '6px',
                      color: t.text,
                      fontSize: '11px',
                      fontFamily: 'SF Mono, Menlo, Consolas, monospace',
                      outline: 'none',
                      resize: 'vertical',
                      minHeight: '60px',
                      opacity: isLoading ? 0.6 : 1,
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = t.accent;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = t.buttonBorder;
                    }}
                  />
                  <div style={{
                    marginTop: '4px',
                    fontSize: '9px',
                    color: t.textDimmed,
                  }}>
                    Must assign result to `result` variable.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Execute / Reset Button */}
          {hasData ? (
            <button
              onClick={() => window.location.reload()}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '8px 12px',
                background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '11px',
                fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s ease',
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              <X size={14} />
              Reset
            </button>
          ) : (
            <button
              onClick={onExecute}
              disabled={isLoading || !selectedFile}
              style={{
                width: '100%',
                padding: '8px 12px',
                background: !isLoading && selectedFile
                  ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                  : t.buttonBg,
                border: 'none',
                borderRadius: '6px',
                color: !isLoading && selectedFile ? '#fff' : t.textMuted,
                fontSize: '11px',
                fontWeight: 600,
                cursor: !isLoading && selectedFile ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s ease',
              }}
            >
              {isLoading ? (
                <>
                  <Loader2 size={14} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
                  Processing...
                </>
              ) : (
                <>
                  <Play size={14} />
                  Load Data
                </>
              )}
            </button>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
